// Layer 3 (Transformation): the GM roll-up strip for the plant model (#169,
// rollup.A1 — Paul A §M4: the same facts compressed to a handful of numbers
// over 24 h / WTD / MTD, checked against target rather than read by tag).

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { ScenarioPhase } from '@/versions/v1/schema/scenario';
import type { UtilizationRank } from '@/versions/v1/schema/unit-operation';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import { phaseFor } from '@/versions/v1/shared/ScenarioEngine';
import { isHardOreZone, oreZoneAt } from '@/versions/v1/shared/bunker-hill-ore-zones';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { valueAt } from '@/versions/v1/shared/series.derive';
import type { WhatIfDerived } from './ProcessGraph.transform';
import {
  recoveryActualPct,
  recoveryDesignPct,
  isPeriodMeasured,
  periodWindows,
  type PeriodView,
} from './MetalAccountingLayer.transform';
import { oreWindowCoverage } from './periodBalance';

const DESIGN = {
  feedStph: 75,
  p80Um: 74,
} as const;

/** K10b (#518): the Plant model feed card's own reading. `series.derive.ts`
 *  re-bases `rd-unit-bm01-feed` onto the measured daily ore
 *  (`rd-kpi-ore-td` ÷ 24), so this GM roll-up feed cell reads the same measured
 *  daily basis the page balance (`feedTonnageForPeriod`) and the feed card use
 *  — never the hourly `rs-wit-021` latest point. The raw hourly re-base stays
 *  deferred past the demo. */
const DAILY_FEED_READING_ID = 'rd-unit-bm01-feed';

const PHASE_P80: Record<ScenarioPhase, number> = {
  idle: 74,
  provisional: 80,
  confirmed: 88,
};

const DENT_PER_UM = { zn: 0.35, pb: 0.3 } as const;

export type RollupWindowCell = {
  window: '24 h' | 'WTD' | 'MTD' | 'Daily average';
  value: string;
  tone: VizTone;
};

export type RollupMetric = {
  id: string;
  label: string;
  cells: readonly RollupWindowCell[];
  target: string;
  synthetic: boolean;
  title: string;
};

export type GmRollupView = {
  metrics: readonly RollupMetric[];
  bottleneck: { label: string; sub: string; tone: VizTone; title: string };
  synTitle: string;
  caption: string;
};

function recoveryTone(dent: number): VizTone {
  if (dent >= 3) return 'bad';
  if (dent >= 1) return 'warn';
  return 'neutral';
}

function recoveryMetric(
  id: string,
  label: string,
  designPct: number,
  dayPct: number,
  wtdPct: number,
  mtdPct: number,
  title: string,
): RollupMetric {
  const dent = designPct - dayPct;
  return {
    id,
    label,
    cells: [
      { window: '24 h', value: `${dayPct.toFixed(1)}%`, tone: recoveryTone(dent) },
      { window: 'WTD', value: `${wtdPct.toFixed(1)}%`, tone: recoveryTone(designPct - wtdPct) },
      { window: 'MTD', value: `${mtdPct.toFixed(1)}%`, tone: recoveryTone(designPct - mtdPct) },
    ],
    target: `vs ${designPct.toFixed(1)}% design`,
    synthetic: true,
    title,
  };
}

/** K10b round 3 (#518): a WTD/MTD feed read from the same `PeriodView` the
 *  balance carries — the window's measured ore tonnage over its measured days
 *  ÷ (days × 24). Never the typed design and never the hourly series. */
type WindowFeedRead = {
  stph: number;
  days: number;
  windowDays: number;
  tonnage: number;
  missingDays: readonly string[];
};

function windowFeedRead(view: PeriodView, period: 'wtd' | 'mtd'): WindowFeedRead | null {
  if (!isPeriodMeasured(view)) return null;
  const coverage = oreWindowCoverage(period);
  if (coverage.measuredDays <= 0) return null;
  return {
    stph: view.feed / (coverage.measuredDays * 24),
    days: coverage.measuredDays,
    windowDays: coverage.windowDays,
    tonnage: view.feed,
    missingDays: coverage.missingDays,
  };
}

function windowFeedDisplay(read: WindowFeedRead | null, decimals: number): string {
  return read ? `${read.stph.toFixed(decimals)} stph` : 'unmeasured';
}

/** Tooltip basis for a WTD/MTD cell: the tonnage, the measured-day count, the
 *  hours it divides by, and any window day with no measured tonnage (null
 *  stamps named, unstamped days counted — never averaged away). */
function windowFeedBasis(label: string, read: WindowFeedRead | null): string {
  if (!read) return `${label}: no measured daily ore point in the window`;
  const tonnes = Math.round(read.tonnage).toLocaleString('en-US');
  const dayWord = read.days === 1 ? 'day' : 'days';
  const absentDays = read.windowDays - read.days - read.missingDays.length;
  let coverage: string;
  if (read.missingDays.length) {
    coverage = `${read.days} of ${read.windowDays} window days measured; no tonnage on ${read.missingDays.join(', ')}`;
  } else if (absentDays > 0) {
    coverage = `${read.days} of ${read.windowDays} window days measured; ${absentDays} day(s) have no stamped tonnage`;
  } else {
    coverage = `every one of the ${read.windowDays} window days measured`;
  }
  return `${label} ${tonnes} dst over ${read.days} measured ${dayWord} ÷ ${read.days * 24} h; ${coverage}`;
}

/** komatsu.F11 (#737): a route whose own strip already prints a band metric
 *  drops that cell here, so one screen never carries the figure twice. The
 *  band's anatomy is otherwise unchanged and every other caller passes nothing. */
export type GmRollupOptions = {
  omitMetrics?: readonly string[];
};

export function gmRollupFromViews(
  state: SessionState,
  ranks: readonly UtilizationRank[],
  windows: { day: PeriodView; wtd: PeriodView; mtd: PeriodView },
  options: GmRollupOptions = {},
): GmRollupView {
  const accountingState = { ...state, whatIf: null };
  const phase = phaseFor(accountingState);
  const p80 = PHASE_P80[phase];
  const coarsening = Math.max(0, p80 - DESIGN.p80Um);
  const znDesign = recoveryDesignPct('Zn') ?? 85.1;
  const pbDesign = recoveryDesignPct('Pb') ?? 88.2;
  const znDay = recoveryActualPct('Zn', windows.day) ?? znDesign - DENT_PER_UM.zn * coarsening;
  const pbDay = recoveryActualPct('Pb', windows.day) ?? pbDesign - DENT_PER_UM.pb * coarsening;
  const feedReading = readingByIdLookup(DAILY_FEED_READING_ID);
  const feedRaw = valueAt(DAILY_FEED_READING_ID, accountingState);
  const feedDecimals = feedReading?.decimals ?? 1;
  const feedDesign = feedReading?.design ?? DESIGN.feedStph;
  const feedValue = feedRaw ?? feedDesign;
  const feedDisplay = `${feedValue.toFixed(feedDecimals)} stph`;
  const feedDesignDisplay = `${feedDesign.toFixed(feedDecimals)} stph`;

  const znWtd = recoveryActualPct('Zn', windows.wtd) ?? znDay;
  const znMtd = recoveryActualPct('Zn', windows.mtd) ?? znDay;
  const pbWtd = recoveryActualPct('Pb', windows.wtd) ?? pbDay;
  const pbMtd = recoveryActualPct('Pb', windows.mtd) ?? pbDay;

  const recoveryTitle =
    '24 h / WTD / MTD from the same recovery series as the KPI row (`recoveryActualPct`); metal-accounting never re-derives from what-if.';

  // K10b round 3 (#518): WTD / MTD read the same measured daily ore basis the
  // day cell and the balance use — the window's PeriodView tonnage ÷ (measured
  // days × 24) — instead of the typed 75 stph design shown as if measured.
  const wtdFeed = windowFeedRead(windows.wtd, 'wtd');
  const mtdFeed = windowFeedRead(windows.mtd, 'mtd');
  const feedToneFor = (value: number | null): VizTone => {
    if (value === null) return 'neutral';
    return `${value.toFixed(feedDecimals)} stph` === feedDesignDisplay ? 'neutral' : 'warn';
  };
  const feedTone = feedToneFor(feedValue);
  const feedMetric: RollupMetric = {
    id: 'rollup-feed',
    label: 'Mill feed',
    cells: [
      { window: 'Daily average', value: feedDisplay, tone: feedTone },
      { window: 'WTD', value: windowFeedDisplay(wtdFeed, feedDecimals), tone: feedToneFor(wtdFeed?.stph ?? null) },
      { window: 'MTD', value: windowFeedDisplay(mtdFeed, feedDecimals), tone: feedToneFor(mtdFeed?.stph ?? null) },
    ],
    target: `vs ${feedDesignDisplay} design`,
    synthetic: true,
    title:
      `Measured daily ore basis (${DAILY_FEED_READING_ID} = rd-kpi-ore-td ÷ 24): daily average ${feedDisplay}. ` +
      `${windowFeedBasis('WTD', wtdFeed)}. ${windowFeedBasis('MTD', mtdFeed)}. ` +
      'The WTD / MTD window runs from the plant week / month boundary to the session cursor; the tonnage is the same PeriodView feed the balance consumes.',
  };

  const bottleneckRank = ranks.find((rank) => rank.isBottleneck) ?? ranks[0];
  const utilPct = bottleneckRank?.utilizationPct ?? null;
  const bottleneckTone: VizTone =
    utilPct != null && utilPct >= 94 ? 'bad' : utilPct != null && utilPct >= 88 ? 'warn' : 'neutral';
  const cursorZone = oreZoneAt(accountingState.cursor);
  // K10b round 3 (#518): the default grinding share is the measured daily feed
  // over the band top (71.666667 / 90 = 80%), the same daily basis the feed cell
  // beside it reads — so the basis label matches that cell, not the hourly "24 h"
  // window the deferred `rs-wit-021` re-base still measures (75 / 90 = 83%).
  const utilLine = utilPct != null ? `${utilPct}% of band (daily average)` : 'daily average';
  const bottleneckSub =
    cursorZone && isHardOreZone(cursorZone)
      ? `${utilLine} · ${cursorZone.label}`
      : utilLine;

  return {
    metrics: [
      recoveryMetric('rollup-zn-rec', 'Zn recovery', znDesign, znDay, znWtd, znMtd, recoveryTitle),
      recoveryMetric('rollup-pb-rec', 'Pb recovery', pbDesign, pbDay, pbWtd, pbMtd, recoveryTitle),
      feedMetric,
    ].filter((metric) => !(options.omitMetrics ?? []).includes(metric.id)),
    bottleneck: {
      label: bottleneckRank?.name ?? 'Grinding',
      sub: bottleneckSub,
      tone: bottleneckTone,
      title: bottleneckRank
        ? `${bottleneckRank.name} — ${bottleneckRank.basis}`
        : 'Phase-applied utilization ranking',
    },
    synTitle:
      'Windowed roll-up from the seeded series at the session cursor — one story day, not historian aggregates.',
    caption: '',
  };
}

/** Ingestion-route wrapper: applies what-if on top of the PeriodView roll-up. */
export function gmRollup(
  state: SessionState,
  ranks: readonly UtilizationRank[],
  whatIf: WhatIfDerived | null,
): GmRollupView {
  if (!whatIf) return gmRollupFromViews(state, ranks, periodWindows());

  const znDesign = recoveryDesignPct('Zn') ?? 85.1;
  const pbDesign = recoveryDesignPct('Pb') ?? 88.2;
  const znDay = whatIf.znRecPct;
  const pbDay = whatIf.pbRecPct;
  const feedRaw = Math.round(DESIGN.feedStph * whatIf.scale);

  const znWtd = znDay;
  const znMtd = znDay;
  const pbWtd = pbDay;
  const pbMtd = pbDay;

  const recoveryTitle =
    '24 h / WTD / MTD from the same recovery series as the KPI row (`recoveryActualPct`); what-if re-derives from coarsening.';

  const feedTone: VizTone = feedRaw === DESIGN.feedStph ? 'neutral' : 'warn';
  const feedMetric: RollupMetric = {
    id: 'rollup-feed',
    label: 'Mill feed',
    cells: [
      { window: '24 h', value: `${feedRaw} stph`, tone: feedTone },
      { window: 'WTD', value: `${DESIGN.feedStph} stph`, tone: 'neutral' },
      { window: 'MTD', value: `${DESIGN.feedStph} stph`, tone: 'neutral' },
    ],
    target: `vs ${DESIGN.feedStph} stph design`,
    synthetic: true,
    title:
      'Design feed held across windows; WTD / MTD shaping is synthetic.',
  };

  const bottleneckRank = ranks.find((rank) => rank.isBottleneck) ?? ranks[0];
  const utilPct = whatIf.grindUtilPct;
  const bottleneckTone: VizTone =
    utilPct != null && utilPct >= 94 ? 'bad' : utilPct != null && utilPct >= 88 ? 'warn' : 'neutral';
  const cursorZone = oreZoneAt(state.cursor);
  const utilLine = utilPct != null ? `${utilPct}% of band (24 h)` : '24 h';
  const bottleneckSub =
    cursorZone && isHardOreZone(cursorZone)
      ? `${utilLine} · ${cursorZone.label}`
      : utilLine;

  return {
    metrics: [
      recoveryMetric('rollup-zn-rec', 'Zn recovery', znDesign, znDay, znWtd, znMtd, recoveryTitle),
      recoveryMetric('rollup-pb-rec', 'Pb recovery', pbDesign, pbDay, pbWtd, pbMtd, recoveryTitle),
      feedMetric,
    ],
    bottleneck: {
      label: bottleneckRank?.name ?? 'Grinding',
      sub: bottleneckSub,
      tone: bottleneckTone,
      title: bottleneckRank
        ? `${bottleneckRank.name} — ${bottleneckRank.basis}`
        : 'Phase-applied utilization ranking',
    },
    synTitle:
      'Windowed roll-up from the seeded series at the session cursor — one story day, not historian aggregates.',
    caption: '',
  };
}
